import React from "react";
import { Badge } from "@/components/ui/badge";
import { CaseStudyItem } from "@/types/case-study";
import { RichTextDisplay } from "@/components/ui/rich-text-display";
import { filterEmptySections } from "@/utils/caseStudyUtils";
import { ContentBlockRenderer } from "./content-blocks/ContentBlockRenderer";

interface CaseStudyPreviewContentProps {
  caseStudy: CaseStudyItem;
}

export const CaseStudyPreviewContent: React.FC<CaseStudyPreviewContentProps> = ({
  caseStudy,
}) => {
  return (
    <div className="space-y-8">
      {/* Case Study Title */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          {caseStudy.title || "Untitled Case Study"}
        </h1>
        <RichTextDisplay
          content={caseStudy.description || ""}
          className="prose prose-sm max-w-4xl text-gray-600 [&_p]:mb-3 [&_ul]:space-y-1 [&_ol]:space-y-1 [&_li]:leading-relaxed"
          fallbackText="A comprehensive case study showcasing the design process and outcomes."
        />
      </div>

      {/* Cover Image */}
      {caseStudy.cover_image && (
        <div className="w-full aspect-video bg-gray-100 rounded-lg overflow-hidden mb-8">
          <img 
            src={caseStudy.cover_image} 
            alt={caseStudy.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Sections - Only show sections with content */}
      {filterEmptySections(caseStudy.sections)
        .sort((a, b) => a.order - b.order)
        .map((section) => (
        <div key={section.id} className="mb-8">
          {!section.hideTitle && (
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {section.title}
            </h2>
          )}

          {/* Only show RichTextDisplay for sections that have rich text content */}
          {!section.hideTitle && (
            <RichTextDisplay
              content={section.content || ''}
              className="prose prose-sm max-w-none text-gray-700 [&_ul]:space-y-1 [&_ol]:space-y-1 [&_li]:leading-relaxed [&_li_p]:my-0 [&_li_p]:mt-0 [&_li_p]:mb-0"
              fallbackText="No content added yet. Start writing to see your content here."
            />
          )}

          {/* Images */}
          {section.images && section.images.length > 0 && (
            <div className="space-y-4 mt-6">
              {section.images.map((image) => (
                <div key={image.id} className="space-y-2">
                  <div className="w-full min-h-[400px] bg-gray-100 rounded-lg overflow-hidden">
                    <img 
                      src={image.url} 
                      alt={image.caption || "Case study image"}
                      className="w-full h-full min-h-[400px] object-cover"
                    />
                  </div>
                  {image.caption && (
                    <p className="text-sm text-gray-600 text-center">
                      {image.caption}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Content Blocks */}
          {section.content_blocks && section.content_blocks.length > 0 && (
            <div className="space-y-4 mt-6">
              {section.content_blocks
                .sort((a, b) => a.order - b.order)
                .map((block) => (
                  <ContentBlockRenderer
                    key={block.id}
                    block={block}
                    onUpdate={() => {}} // No updates in preview mode
                    onRemove={() => {}} // No removal in preview mode
                    isActive={false} // Not editable in preview mode
                    preview={true} // Show preview mode
                  />
                ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
